import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type ExtrudeType = "block" | "pyramid";

export interface ExtrudeParams {
  type: ExtrudeType;
  size: number;
  depth: number;
  random: boolean;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const Extrude: Filter<ExtrudeParams> = {
  id: "extrude",
  displayName: "Extrude",
  defaultParams: { type: "block", size: 16, depth: 4, random: false },

  apply(input: PixelBuffer, params: ExtrudeParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const size = Math.max(2, Math.round(params.size));
    const depth = Math.max(1, Math.round(params.depth));

    for (let cy = 0; cy < h; cy += size) {
      for (let cx = 0; cx < w; cx += size) {
        let sumR = 0, sumG = 0, sumB = 0, count = 0;
        const endY = Math.min(cy + size, h);
        const endX = Math.min(cx + size, w);

        for (let y = cy; y < endY; y++) {
          for (let x = cx; x < endX; x++) {
            const si = (y * w + x) * 4;
            sumR += src[si]!; sumG += src[si + 1]!; sumB += src[si + 2]!;
            count++;
          }
        }

        const avgR = Math.round(sumR / count);
        const avgG = Math.round(sumG / count);
        const avgB = Math.round(sumB / count);
        const lum = avgR * 0.299 + avgG * 0.587 + avgB * 0.114;

        for (let y = cy; y < endY; y++) {
          for (let x = cx; x < endX; x++) {
            const o = (y * w + x) * 4;
            let shade: number;

            if (params.type === "pyramid") {
              const dx = Math.abs(x - (cx + size / 2)) / (size / 2);
              const dy = Math.abs(y - (cy + size / 2)) / (size / 2);
              const dist = Math.max(dx, dy);
              shade = 1 - dist * (depth / 10);
            } else {
              const relX = (x - cx) / size;
              const relY = (y - cy) / size;
              shade = 1 - (relX + relY) * (depth / 20);
            }

            if (params.random) {
              shade *= 0.7 + Math.random() * 0.3;
            }

            shade = Math.max(0.2, Math.min(1, shade));
            dst[o] = clamp8(Math.round(avgR * shade));
            dst[o + 1] = clamp8(Math.round(avgG * shade));
            dst[o + 2] = clamp8(Math.round(avgB * shade));
            dst[o + 3] = 255;
          }
        }
      }
    }
    return out;
  },
};
